import { useMemo } from 'react';
import { OVERALL_DOCUMENT_STATUS_LABELS } from '../../../constants/peppolStatusConstants';
import { READ_BUSINESS_PROFILE } from '../../../constants/eInvoicePermissions';
import { useGetBusinessProfilesHandler } from '../../businessProfiles/dataHandlers/getBusinessProfilesHandler';
import { usePermissions } from '../../../hooks/usePermissions';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import {
  ALL_FILTER_VALUE,
  EDOC_FILTER_OPTIONS,
  REPORT_TYPE_OPTIONS,
  STATUS_CONCRETE_VALUES,
} from '../constants/documentReportFilterConstants';
import { buildDocTypeOptions, buildTinOptions } from '../utils/documentReportScopeUtils';

const BUSINESS_PROFILES_QUERY = { page: 0, page_size: 100 };

const useDocumentReportFilterOptions = () => {
  const { dashboard, documentReports } = useTranslatedMessages();
  const { loading: permissionsLoading, hasEInvoicePermission } = usePermissions();
  const canReadBusinessProfiles = hasEInvoicePermission(READ_BUSINESS_PROFILE);
  const filterLabels = useMemo(() => documentReports?.filters ?? {}, [documentReports]);
  const statusLabels = useMemo(() => dashboard?.document_status ?? {}, [dashboard]);

  const { businessProfiles, isLoading: profilesLoading } = useGetBusinessProfilesHandler({
    searchQuery: BUSINESS_PROFILES_QUERY,
    enabled: !permissionsLoading && canReadBusinessProfiles,
  });

  const tinOptions = useMemo(() => buildTinOptions(businessProfiles), [businessProfiles]);

  const docTypeOptions = useMemo(
    () => buildDocTypeOptions({ hasEInvoicePermission, filterLabels }),
    [filterLabels, hasEInvoicePermission],
  );

  const statusOptions = useMemo(() => {
    const concreteLabels = {
      PENDING: statusLabels.pending ?? OVERALL_DOCUMENT_STATUS_LABELS.PENDING,
      APPROVED: statusLabels.approved ?? OVERALL_DOCUMENT_STATUS_LABELS.APPROVED,
      REJECTED: statusLabels.rejected ?? OVERALL_DOCUMENT_STATUS_LABELS.REJECTED,
      VALIDATION_FAILED: statusLabels.validation_failed ?? OVERALL_DOCUMENT_STATUS_LABELS.VALIDATION_FAILED,
    };

    return [
      { value: ALL_FILTER_VALUE, label: filterLabels.status_all ?? 'All' },
      ...STATUS_CONCRETE_VALUES.map((value) => ({
        value,
        label: concreteLabels[value] ?? value,
      })),
    ];
  }, [filterLabels, statusLabels]);

  const eDocOptions = useMemo(
    () =>
      EDOC_FILTER_OPTIONS.map((option) => ({
        value: option.value,
        label: filterLabels[option.labelKey] ?? option.value,
      })),
    [filterLabels],
  );

  const reportTypeOptions = useMemo(
    () =>
      REPORT_TYPE_OPTIONS.map((option) => ({
        value: option.value,
        label: filterLabels[option.labelKey] ?? option.value,
      })),
    [filterLabels],
  );

  const categoryLabels = useMemo(
    () => ({
      tin: filterLabels.tin ?? 'TIN',
      docTypes: filterLabels.doc_type ?? 'Doc Type',
      eDoc: filterLabels.edoc ?? 'E-doc',
      status: filterLabels.status ?? 'Status',
      reportType: filterLabels.report_type ?? 'Report Type',
      timePeriod: filterLabels.period ?? 'Period',
    }),
    [filterLabels],
  );

  const timePeriodLabels = useMemo(() => filterLabels.time_period ?? {}, [filterLabels]);

  const disabledFilterLabels = useMemo(
    () => ({
      label: filterLabels.no_access ?? 'No access',
      docTypeTooltip:
        filterLabels.doc_type_no_access_tooltip ?? 'You do not have permission to include this document type.',
    }),
    [filterLabels],
  );

  return useMemo(
    () => ({
      tinOptions,
      docTypeOptions,
      statusOptions,
      eDocOptions,
      reportTypeOptions,
      categoryLabels,
      timePeriodLabels,
      disabledFilterLabels,
      profilesLoading: permissionsLoading || (canReadBusinessProfiles && profilesLoading),
    }),
    [
      canReadBusinessProfiles,
      categoryLabels,
      disabledFilterLabels,
      docTypeOptions,
      eDocOptions,
      permissionsLoading,
      profilesLoading,
      reportTypeOptions,
      statusOptions,
      timePeriodLabels,
      tinOptions,
    ],
  );
};

export default useDocumentReportFilterOptions;
